import React, { useState, useEffect } from 'react';
import { Loader2, ExternalLink, AlertCircle, Info } from 'lucide-react';
import { PLATFORMS_CONFIG } from '../configs/constant';
import { apiService } from '../api/apiclient';

const PostStatusDetail = ({ postId, onShowFeedPreview }) => {
    const [statusList, setStatusList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchStatus = async () => {
            setLoading(true);
            try {
                const res = await apiService.getPublishStatus(postId);
                setStatusList(res.tasks || []);
            } catch (err) {
                setError("Failed to load detail");
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        if (postId) fetchStatus();
    }, [postId]);

    if (loading) return (
        <div className="flex items-center justify-center p-4">
            <Loader2 size={16} className="animate-spin text-red-500 mr-2" />
            <span className="text-xs text-gray-400">Fetching release status...</span>
        </div>
    );

    if (error) return (
        <div className="flex items-center text-red-400 p-2 text-xs bg-red-50 rounded-lg">
            <AlertCircle size={14} className="mr-1" /> {error}
        </div>
    );

    return (
        <div className="mt-4 pt-4 border-t border-gray-100 flex flex-col gap-2">
            <div className="flex justify-between items-center mb-1">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Account Release List</p>
                <span className="text-[10px] text-gray-300">Total {statusList.length} tasks</span>
            </div>

            {statusList.map((item, idx) => {
                const config = PLATFORMS_CONFIG[item.platform];
                const isSuccess = item.status === 'success' || item.status === 'completed';
                const isQueued = item.status === 'queued' || item.status === 'processing' || item.status === 'media_processing';
                const isFailed = item.status === 'failed';

                return (
                    <div key={idx} className="flex items-center justify-between bg-gray-50/50 p-2.5 rounded-xl border border-gray-100 text-xs transition hover:bg-white hover:shadow-sm">
                        <div className="flex items-center gap-3">
                            <div className={`${config?.color || 'bg-gray-400'} w-7 h-7 rounded-lg flex items-center justify-center text-white shadow-sm`}>
                                <i className={config?.icon}></i>
                            </div>
                            <div className="flex flex-col">
                                <span className="font-bold text-gray-700">{item.account_name || 'Unknown Account'}</span>
                                <span className="text-[10px] text-gray-400 uppercase">{item.platform}</span>
                            </div>
                        </div>

                        <div className="flex items-center gap-4">
                            {isSuccess && (
                                <>
                                    <div className="flex flex-col items-end">
                                        <span className="text-green-500 font-bold text-[10px]">Publishing Sucessful</span>
                                    </div>

                                    <ExternalLink
                                        size={14}
                                        className="cursor-pointer text-red-600 hover:text-red-800"
                                        onClick={() =>
                                            onShowFeedPreview(item.platform_post_id, item.account_id)
                                        }
                                    />
                                </>
                            )}
                            {isQueued && (
                                <>
                                    <div className="flex flex-col items-end">
                                        <div className="flex items-center gap-1.5">
                                            <Loader2 size={10} className="animate-spin text-orange-400" />
                                            <span className="text-orange-400 font-bold text-[10px] uppercase">Processing...</span>
                                        </div>
                                    </div>
                                    <Loader2 size={14} className="animate-spin text-orange-400 opacity-70" />
                                </>
                            )}

                            {isFailed && (
                                <>
                                    <div>
                                        <span className="text-red-400 font-bold text-[10px] block">
                                            Publishing Failed
                                        </span>
                                    </div>

                                    <Info
                                        size={14}
                                        className="text-red-400 cursor-pointer hover:text-red-600"
                                        onClick={() => alert(item.error_msg)}
                                    />
                                </>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default PostStatusDetail;